'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { supabase } from '@/lib/supabaseClient';

interface TimeSlot {
  id: string;
  time: string;
  available: boolean;
}

interface ConsultationBookingProps {
  className?: string;
}

const ConsultationBooking = ({ className = '' }: ConsultationBookingProps) => {
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [consultationType, setConsultationType] = useState<string>('phone');
  const initialBookingData = {
    name: '',
    email: '',
    phone: '',
    consultationType: 'phone',
    topic: '',
    notes: '',
  };

  const [bookingData, setBookingData] = useState(initialBookingData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  // Generate next 14 days for booking
  const getAvailableDates = () => {
    const dates = [];
    const today = new Date();

    for (let i = 1; i <= 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);

      // Skip Sundays
      if (date.getDay() !== 0) {
        dates.push({
          value: date.toISOString().split('T')[0],
          label: date.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
          }),
          dayName: date.toLocaleDateString('en-US', { weekday: 'long' }),
        });
      }
    }

    return dates;
  };

  const getTimeSlots = (date: string): TimeSlot[] => {
    const slots: TimeSlot[] = [];
    const selectedDateObj = new Date(date);
    const dayOfWeek = selectedDateObj.getDay();

    // Different hours for different days
    const startHour = dayOfWeek === 6 ? 9 : 8; // Saturday starts at 9, others at 8
    const endHour = dayOfWeek === 6 ? 17 : 20; // Saturday ends at 5, others at 8

    for (let hour = startHour; hour < endHour; hour++) {
      // Skip lunch hour (12-1 PM)
      if (hour === 12) continue;

      const time12 = hour > 12 ? `${hour - 12}:00 PM` : `${hour}:00 AM`;
      const timeId = `${hour}:00`;

      slots.push({
        id: timeId,
        time: time12,
        available: Math.random() > 0.3, // 70% availability simulation
      });
    }

    return slots;
  };

  const consultationTypes = [
    {
      id: 'phone',
      name: 'Phone Consultation',
      icon: 'PhoneIcon',
      duration: '30 minutes',
      description: 'Discuss your needs over the phone',
    },
    {
      id: 'video',
      name: 'Video Call',
      icon: 'VideoCameraIcon',
      duration: '45 minutes',
      description: 'Face-to-face consultation via video',
    },
    {
      id: 'property',
      name: 'Property Visit',
      icon: 'HomeIcon',
      duration: '90 minutes',
      description: 'On-site property evaluation',
    },
  ];

  const consultationTopics = [
    'Property Valuation',
    'Selling Process',
    'Investment Opportunities',
    'Market Analysis',
    'Legal Questions',
    'Financing Options',
    'Partnership Opportunities',
    'Other',
  ];

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setBookingData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSubmitted(false);

    try {
      const { error: submitError } = await supabase.from('consultation_requests').insert({
        name: bookingData.name,
        full_name: bookingData.name,
        email: bookingData.email,
        phone: bookingData.phone,
        consultation_type: consultationType,
        topic: bookingData.topic || null,
        notes: bookingData.notes || null,
        requested_date: selectedDate,
        requested_time: selectedTime,
        preferred_date: selectedDate,
        preferred_time: selectedTime,
      });

      if (submitError) {
        throw submitError;
      }

      setSubmitted(true);
      setBookingData(initialBookingData);
      setSelectedDate('');
      setSelectedTime('');
      setConsultationType('phone');
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to book consultation. Please try again.';
      console.error('Error submitting booking form:', err);
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const availableDates = getAvailableDates();
  const timeSlots = selectedDate ? getTimeSlots(selectedDate) : [];

  return (
    <section className={`py-16 lg:py-24 bg-surface ${className}`}>
      <div className="max-w-6xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
            Schedule a Consultation
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Book a personalized consultation with our real estate experts. Choose the format and
            time that works best for you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Consultation Types */}
          <div className="space-y-6">
            <h3 className="text-xl font-heading font-semibold text-foreground mb-4">
              Choose Consultation Type
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {consultationTypes.map((type) => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => setConsultationType(type.id)}
                  className={`p-4 border rounded-lg text-left silk-smooth ${
                    consultationType === type.id
                      ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                      : 'border-border bg-card hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <Icon
                      name={type.icon as any}
                      size={24}
                      className={
                        consultationType === type.id ? 'text-primary' : 'text-muted-foreground'
                      }
                    />
                    <div className="flex-1">
                      <h4 className="font-medium text-foreground mb-1">{type.name}</h4>
                      <p className="text-sm text-accent font-medium mb-1">{type.duration}</p>
                      <p className="text-xs text-muted-foreground">{type.description}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Date Selection */}
            <div>
              <h3 className="text-xl font-heading font-semibold text-foreground mb-4">
                Select Date
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {availableDates.slice(0, 12).map((date) => (
                  <button
                    key={date.value}
                    type="button"
                    onClick={() => setSelectedDate(date.value)}
                    className={`p-3 border rounded-md text-sm silk-smooth ${
                      selectedDate === date.value
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-card hover:border-primary/50 text-foreground'
                    }`}
                  >
                    <div className="font-medium">{date.label}</div>
                    <div className="text-xs opacity-75">{date.dayName}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Time Selection */}
            {selectedDate && (
              <div>
                <h3 className="text-xl font-heading font-semibold text-foreground mb-4">
                  Available Times
                </h3>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {timeSlots.map((slot) => (
                    <button
                      key={slot.id}
                      type="button"
                      onClick={() => slot.available && setSelectedTime(slot.id)}
                      disabled={!slot.available}
                      className={`p-2 border rounded text-sm silk-smooth ${
                        selectedTime === slot.id
                          ? 'border-primary bg-primary text-primary-foreground'
                          : slot.available
                            ? 'border-border bg-card hover:border-primary/50 text-foreground'
                            : 'border-border bg-muted text-muted-foreground cursor-not-allowed opacity-50'
                      }`}
                    >
                      {slot.time}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Booking Form */}
          <div className="bg-card rounded-lg border border-border p-6 luxury-shadow">
            <h3 className="text-xl font-heading font-semibold text-foreground mb-6">
              Booking Details
            </h3>

            <form onSubmit={handleBooking} className="space-y-4">
              {submitted && (
                <div className="rounded-md border border-success/30 bg-success/10 p-4 text-sm text-success">
                  Thank you. Your consultation request was sent successfully.
                </div>
              )}

              {error && (
                <div className="rounded-md border border-error/30 bg-error/10 p-4 text-sm text-error">
                  {error}
                </div>
              )}
              <div>
                <label
                  htmlFor="booking-name"
                  className="block text-sm font-medium text-foreground mb-2"
                >
                  Full Name *
                </label>
                <input
                  type="text"
                  id="booking-name"
                  name="name"
                  value={bookingData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent silk-smooth"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label
                  htmlFor="booking-email"
                  className="block text-sm font-medium text-foreground mb-2"
                >
                  Email Address *
                </label>
                <input
                  type="email"
                  id="booking-email"
                  name="email"
                  value={bookingData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent silk-smooth"
                  placeholder="your.email@example.com"
                />
              </div>

              <div>
                <label
                  htmlFor="booking-phone"
                  className="block text-sm font-medium text-foreground mb-2"
                >
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="booking-phone"
                  name="phone"
                  value={bookingData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent silk-smooth"
                  placeholder="Your phone number"
                />
              </div>

              <div>
                <label
                  htmlFor="booking-topic"
                  className="block text-sm font-medium text-foreground mb-2"
                >
                  Consultation Topic
                </label>
                <select
                  id="booking-topic"
                  name="topic"
                  value={bookingData.topic}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent silk-smooth"
                >
                  <option value="">Select a topic</option>
                  {consultationTopics.map((topic) => (
                    <option key={topic} value={topic}>
                      {topic}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="booking-notes"
                  className="block text-sm font-medium text-foreground mb-2"
                >
                  Additional Notes
                </label>
                <textarea
                  id="booking-notes"
                  name="notes"
                  value={bookingData.notes}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent silk-smooth resize-vertical"
                  placeholder="Any specific questions or details you'd like to discuss..."
                />
              </div>

              {/* Booking Summary */}
              {selectedDate && selectedTime && (
                <div className="bg-muted p-4 rounded-md">
                  <h4 className="font-medium text-foreground mb-2">Booking Summary</h4>
                  <div className="space-y-1 text-sm text-muted-foreground">
                    <p>
                      <strong>Type:</strong>{' '}
                      {consultationTypes.find((t) => t.id === consultationType)?.name}
                    </p>
                    <p>
                      <strong>Date:</strong>{' '}
                      {new Date(selectedDate).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </p>
                    <p>
                      <strong>Time:</strong> {timeSlots.find((t) => t.id === selectedTime)?.time}
                    </p>
                    <p>
                      <strong>Duration:</strong>{' '}
                      {consultationTypes.find((t) => t.id === consultationType)?.duration}
                    </p>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={
                  loading ||
                  !selectedDate ||
                  !selectedTime ||
                  !bookingData.name ||
                  !bookingData.email ||
                  !bookingData.phone
                }
                className="w-full bg-primary text-primary-foreground px-4 py-3 rounded-md font-medium silk-smooth hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed shadow-button"
              >
                <Icon name="CalendarIcon" size={20} className="inline mr-2" />
                {loading ? 'Booking...' : 'Book Consultation'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ConsultationBooking;
